/******************************************************************************
 * Created Date: Thursday March 10th 2022                                     *
 * Author: Patrick B.                                                         *
 * -----                                                                      *
 * Last Modified: Wed Mar 16 2022                                             *
 * Modified By: Patrick B.                                                    *
 * -----                                                                      *
 * File: /src/components/common/JsonFileUploader.js                           *
 * Copyright (c) 2022 MultiSensor                                             *
 ******************************************************************************/

import {
  Clear as ClearIcon,
  CloudUpload as UploadIcon
} from "@mui/icons-material";
import { Button, Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import PropTypes from "prop-types";
import { useRef } from "react";

const PREFIX = "JsonFileUploader";

const classes = {
  root: `${PREFIX}-root`,
  uploadWrapper: `${PREFIX}-uploadWrapper`,
  resetWrapper: `${PREFIX}-resetWrapper`,
  uploadButton: `${PREFIX}-uploadButton`,
  uploadIcon: `${PREFIX}-uploadIcon`,
  clearIcon: `${PREFIX}-clearIcon`
};

const Root = styled("div")(({ theme }) => ({
  [`.${classes.root}`]: {
    display: "flex",
    justifyContent: "center"
  },

  [`.${classes.uploadWrapper}`]: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center"
  },

  [`.${classes.resetWrapper}`]: {
    marginLeft: "20px"
  },

  [`.${classes.uploadButton}`]: {
    display: "flex",
    marginBottom: "5px"
  },

  [`.${classes.uploadIcon}`]: {
    marginLeft: "10px"
  },

  [`.${classes.clearIcon}`]: {
    marginLeft: "5px"
  }
}));

const JsonFileUploader = ({ setJson, initialJson }) => {
  const inputref = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  const handleUpload = e => {
    if (!e.target.value) {
      setJson(initialJson);
      return;
    }

    const fileReader = new FileReader();
    fileReader.readAsText(e.target.files[0], "UTF-8");
    fileReader.onload = e => {
      const jsonObject = JSON.parse(e.target.result);
      if (jsonObject.settings) {
        setJson(jsonObject.settings);
      } else {
        setJson(jsonObject);
      }
      console.log("UPLOADED JSON:", jsonObject.settings ?? jsonObject);
    };

    enqueueSnackbar(`${e.target.files[0].name} UPLOADED!`, {
      variant: "success"
    });
  };

  const handleReset = () => {
    if (inputref.current.value) {
      inputref.current.value = "";
    }
    setJson(initialJson);
  };

  return (
    <Root className={classes.root}>
      <div className={classes.uploadWrapper}>
        <Tooltip title="Upload custom JSON file">
          <Button
            variant="contained"
            component="label"
            color="primary"
            className={classes.uploadButton}
          >
            Upload json
            <UploadIcon className={classes.uploadIcon} />
            <input
              ref={inputref}
              type="file"
              accept="application/json"
              hidden
              onChange={e => handleUpload(e)}
            />
          </Button>
        </Tooltip>
        <Typography variant="subtitle2">
          {inputref?.current?.files[0]?.name ?? "No file Chosen"}
        </Typography>
      </div>
      <div className={classes.resetWrapper}>
        <Button variant="outlined" onClick={handleReset}>
          Reset
          <ClearIcon fontSize="small" className={classes.clearIcon} />
        </Button>
      </div>
    </Root>
  );
};

JsonFileUploader.propTypes = {
  setJson: PropTypes.func.isRequired,
  initialJson: PropTypes.object.isRequired
};

export default JsonFileUploader;
